import React, { useEffect, useRef } from 'react';
import { stats, LOGO } from '../mock';
import { ArrowRight, Building2, Award } from 'lucide-react';
import useReveal from '../hooks/useReveal';

export default function Hero() {
  const sceneRef = useRef(null);
  const layerRef = useRef(null);
  const [heroRef, heroIn] = useReveal(0.05);

  useEffect(() => {
    const onMove = (e) => {
      if (!sceneRef.current || !layerRef.current) return;
      const rect = sceneRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      layerRef.current.style.transform = `translate3d(${x * -24}px, ${y * -16}px, 0)`;
    };
    const el = sceneRef.current;
    if (el) el.addEventListener('mousemove', onMove);
    return () => { if (el) el.removeEventListener('mousemove', onMove); };
  }, []);

  return (
    <section id="overview" className="section studio-light" ref={heroRef} style={{ paddingTop: 180, paddingBottom: 60, overflow: 'hidden', position: 'relative' }}>
      <div className="container-x relative" style={{ zIndex: 3 }}>
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left content */}
          <div className="lg:col-span-6">
            <div className={`reveal ${heroIn ? 'in-view' : ''}`}>
              <div className="eyebrow" style={{ marginBottom: 32 }}>
                Corporate Signage Studio · Est. Bengaluru
              </div>
            </div>

            <div className={`reveal delay-1 ${heroIn ? 'in-view' : ''}`}>
              <h1 className="font-display" style={{ fontSize: 'clamp(56px, 8.5vw, 112px)', color: 'var(--paper)', marginBottom: 30 }}>
                We build the<br />
                <em style={{ color: 'var(--teal-light)', fontStyle: 'italic', fontFamily: 'Fraunces', fontWeight: 500 }}>signs</em> that build<br />
                your <em style={{ color: 'var(--orange)', fontStyle: 'italic', fontFamily: 'Fraunces', fontWeight: 500 }}>brand</em>.
              </h1>
            </div>

            <div className={`reveal delay-2 ${heroIn ? 'in-view' : ''}`}>
              <p style={{ color: 'var(--smoke)', fontSize: 17, lineHeight: 1.65, maxWidth: 500, marginBottom: 44 }}>
                A premium corporate signage &amp; branding studio crafting dimensional letters, lobby monoliths, LED boards, wall graphics, and turnkey rollouts — delivered nationwide.
              </p>
            </div>

            <div className={`reveal delay-3 ${heroIn ? 'in-view' : ''}`}>
              <div className="flex flex-wrap gap-4" style={{ marginBottom: 60 }}>
                <a href="#services" className="btn-primary" style={{ textDecoration: 'none' }}>
                  Explore Services <ArrowRight size={16} strokeWidth={2.5} />
                </a>
                <a href="#work" className="btn-ghost" style={{ textDecoration: 'none' }}>See Our Work</a>
              </div>
            </div>

            <div className={`reveal delay-4 ${heroIn ? 'in-view' : ''}`}>
              <div className="grid grid-cols-3 gap-6" style={{ maxWidth: 520, borderTop: '1px solid var(--line)', paddingTop: 30 }}>
                {stats.map((s, i) => (
                  <div key={s.label}>
                    <div className={`stat-big ${i === 1 ? 'orange' : ''}`}>{s.value}</div>
                    <div className="font-mono" style={{ color: 'var(--muted)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', marginTop: 6 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right 3D scene */}
          <div className="lg:col-span-6">
            <div ref={sceneRef} style={{ position: 'relative', height: 760, perspective: 1600 }}>
              <div ref={layerRef} style={{ position: 'absolute', inset: 0, transformStyle: 'preserve-3d', transition: 'transform 0.5s cubic-bezier(.2,.8,.2,1)' }}>

                {/* Ceiling rail */}
                <div style={{ position: 'absolute', top: 20, left: '5%', right: '5%', height: 2, background: 'linear-gradient(90deg, transparent, var(--brass), transparent)', opacity: 0.5 }} />

                {/* Hanging chain sign with real logo */}
                <div style={{ position: 'absolute', top: 90, left: '10%', width: 460 }}>
                  <div className="chain-sign">
                    <div className="chain-bracket left" />
                    <div className="chain-bracket right" />
                    <div className="chain left" />
                    <div className="chain right" />
                    <div className="sign-board" style={{ padding: '44px 40px' }}>
                      <div className="sign-rivet" style={{ top: 12, left: 12 }} />
                      <div className="sign-rivet" style={{ top: 12, right: 12 }} />
                      <div className="sign-rivet" style={{ bottom: 12, left: 12 }} />
                      <div className="sign-rivet" style={{ bottom: 12, right: 12 }} />
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          width: 220, height: 220, margin: '0 auto',
                          background: 'var(--warm-white)',
                          borderRadius: 14,
                          padding: 10,
                          boxShadow: '0 12px 30px -8px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(31,42,46,0.06)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <img src={LOGO} alt="Vistora logo" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                        </div>
                        <div style={{ height: 1, background: 'var(--teal)', margin: '20px auto 10px', width: 100, opacity: 0.6 }} />
                        <div className="font-mono" style={{ color: 'var(--teal-light)', fontSize: 10, letterSpacing: '0.28em' }}>CORPORATE SIGNAGE STUDIO</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Channel letter V - top right, positioned to be fully visible */}
                <div className="float-y" style={{ position: 'absolute', top: 24, right: 46, transform: 'rotate(-8deg)' }}>
                  <span className="channel-letter" style={{ fontSize: 96 }}>V</span>
                </div>

                {/* Neon script - shifted down */}
                <div className="float-y delay" style={{ position: 'absolute', top: 150, right: -14, background: 'var(--ink-2)', border: '1px solid var(--teal)', padding: '14px 22px', transform: 'rotate(4deg)', boxShadow: '0 20px 40px -20px rgba(0,0,0,0.8), 0 0 30px -10px rgba(79,191,184,0.4)', borderRadius: 4 }}>
                  <span className="neon-sign" style={{ fontSize: 22 }}>Crafted with care</span>
                </div>

                {/* Corporate monolith (freestanding lobby sign) */}
                <div style={{ position: 'absolute', bottom: 20, left: 4, width: 150 }}>
                  <div className="monolith">
                    <div style={{ textAlign: 'center' }}>
                      <img src={LOGO} alt="" style={{ width: 42, height: 42, borderRadius: 6, background: 'var(--warm-white)', padding: 3, marginBottom: 10 }} />
                      <div className="font-display" style={{ color: 'var(--warm-white)', fontSize: 15, letterSpacing: '0.14em' }}>VISTORA</div>
                      <div style={{ height: 1, background: 'rgba(255,255,255,0.3)', margin: '10px auto', width: 40 }} />
                      <div className="font-mono" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 8, letterSpacing: '0.2em' }}>LOBBY · FLR-01</div>
                      <div style={{ marginTop: 18, paddingTop: 12, borderTop: '1px dashed rgba(255,255,255,0.2)' }}>
                        <div className="font-mono" style={{ color: 'var(--orange-2)', fontSize: 9, letterSpacing: '0.18em' }}>→ RECEPTION</div>
                        <div className="font-mono" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 9, letterSpacing: '0.18em', marginTop: 6 }}>→ BOARDROOM</div>
                      </div>
                    </div>
                  </div>
                  <div className="floor-shadow" style={{ bottom: -20 }} />
                </div>

                {/* Reception desk sign - moved to mid-right */}
                <div style={{ position: 'absolute', bottom: 130, right: 20, transform: 'rotate(-3deg)' }}>
                  <div className="reception-sign" style={{ padding: '22px 32px' }}>
                    <div className="font-mono" style={{ color: 'var(--teal-2)', fontSize: 9, letterSpacing: '0.24em', textAlign: 'center', marginBottom: 8 }}>RECEPTION</div>
                    <div className="font-display" style={{ color: 'var(--ink)', fontSize: 24, textAlign: 'center', letterSpacing: '-0.01em' }}>Welcome</div>
                    <div style={{ height: 2, background: 'var(--orange)', width: 40, margin: '10px auto 0' }} />
                  </div>
                </div>

                {/* Boardroom door plaque - bottom center between monolith and reception */}
                <div className="float-y" style={{ position: 'absolute', bottom: 30, left: 175, zIndex: 5 }}>
                  <div className="door-plaque">
                    <div className="screw" style={{ top: 4, left: 4 }} />
                    <div className="screw" style={{ top: 4, right: 4 }} />
                    <div className="screw" style={{ bottom: 4, left: 4 }} />
                    <div className="screw" style={{ bottom: 4, right: 4 }} />
                    <div className="font-mono" style={{ fontSize: 8, letterSpacing: '0.24em', color: 'rgba(31,42,46,0.7)' }}>ROOM 302</div>
                    <div className="font-display" style={{ fontSize: 16, letterSpacing: '0.06em' }}>BOARDROOM</div>
                  </div>
                </div>

                {/* Small quality badge sticker */}
                <div className="vinyl-peel float-y delay" style={{ position: 'absolute', bottom: 30, right: 180, width: 120, transform: 'rotate(-6deg)', zIndex: 4 }}>
                  <div className="font-mono" style={{ fontSize: 9, letterSpacing: '0.22em', marginBottom: 4 }}>QUALITY ASSURED</div>
                  <div className="font-display" style={{ fontSize: 28, lineHeight: 1 }}>100%</div>
                  <div className="font-serif-i" style={{ fontSize: 11, marginTop: 4 }}>hand-checked</div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom ticker */}
      <div className="ticker" style={{ marginTop: 100, position: 'relative', zIndex: 3 }}>
        <div className="ticker-track">
          {[...Array(2)].flatMap((_, k) => (
            ['Corporate Lobbies', 'Channel Letters', 'ACP Facades', 'LED Boards', 'Wall Graphics', 'Wayfinding', 'Turnkey Rollouts', 'Pan-India Install'].map((t, i) => (
              <span key={`${k}-${i}`}>{t} <span style={{ color: 'var(--ink)', opacity: 0.4 }}>✦</span></span>
            ))
          ))}
        </div>
      </div>
    </section>
  );
}
